import { useEffect, useMemo, useRef, useState } from 'react';

import type { UseYMapsProps, YMapsApi } from '../types';
import { MapInitStatus, MapLanguage } from '../constants';
import { loadYMaps } from '../loadYMaps';

/**
 * Хук для подключения Яндекс карт.
 * @returns Объект, содержащий базовые компоненты карт, статус инициализации, текст ошибки (наличии) и опционально загруженные модули и пакеты
 * @kind helper
 */
export const useYMaps = ({
    mapsApiKey,
    language = MapLanguage.ru_RU,
    cspNonce,
    addons,
    apiKeysConfig,
    queryParams = {},
}: UseYMapsProps): YMapsApi => {
    const maps = useRef<YMapsApi['maps']>(null);
    const [status, setStatus] = useState<YMapsApi['status']>(MapInitStatus.NOT_INITIALIZED);
    const error = useRef<YMapsApi['error']>(null);
    const [loadedAddons, setLoadedAddons] = useState<YMapsApi['addons']>(null);

    const urlSearchParams = useMemo(() => {
        const searchParams = new URLSearchParams(queryParams);

        searchParams.append('apikey', mapsApiKey);
        searchParams.append('lang', language);

        return searchParams.toString();
    }, [language, mapsApiKey, queryParams]);

    const mapSrc = `https://api-maps.yandex.ru/v3/?${urlSearchParams.toString()}`;

    const reactify = useRef<YMapsApi['reactify']>(null);

    useEffect(() => {
        loadYMaps({ src: mapSrc, cspNonce: cspNonce, addons }).then(response => {
            if (response.status === MapInitStatus.READY) {
                maps.current = response.maps;
                reactify.current = response.reactify;
                setStatus(MapInitStatus.READY);
                setLoadedAddons(response.addons);
                if (response.maps && apiKeysConfig) {
                    response.maps.getDefaultConfig().setApikeys(apiKeysConfig);
                }
            } else {
                error.current = response.error;
                setStatus(MapInitStatus.ERROR);
            }
        });
    }, [addons, apiKeysConfig, cspNonce, mapSrc]);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    return useMemo(
        () => ({
            status,
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            maps: maps.current,
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            error: error.current,
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            reactify: reactify.current,
            addons: loadedAddons,
        }),
        [status, maps, error, loadedAddons]
    );
};
